<template>
  <!-- Main content section -->
  <div class="flex-1 overflow-hidden bg-gray-50 rounded-xl">
      <div class="h-full overflow-y-auto">
        <!-- Loading State (only when no data exists) -->
        <div v-if="store.loading && !store.hasRequests" class="flex justify-center items-center h-32">
          <LoaderComponent />
        </div>

        <!-- Empty State -->
        <div
          v-else-if="!store.loading && !store.hasRequests"
          class="flex flex-col items-center justify-center h-32 text-gray-500"
        >
          <span class="text-lg">No referral requests found</span>
          <span class="text-sm mt-1">Try adjusting your search criteria</span>
        </div>

        <!-- Table (shown even when loading if data exists) -->
        <div 
          v-if="store.hasRequests"
          class="bg-white rounded-lg overflow-hidden relative transition-opacity duration-200"
          :class="{ 'opacity-50 pointer-events-none': store.loading }"
        >
          <!-- Loading overlay indicator -->
          <div 
            v-if="store.loading" 
            class="absolute top-2 right-2 z-10 bg-white rounded-full shadow-md p-2"
          >
            <Loader2 class="w-4 h-4 animate-spin text-gray-600" />
          </div>
          
          <table class="min-w-full">
            <thead>
              <tr class="bg-white border-b border-gray-200">
                <th class="px-4 py-3 text-left text-xs font-normal text-black text-opacity-80 w-12 cursor-pointer hover:bg-gray-50 select-none" @click="handleSort('created_at')">
                  <div class="flex items-center gap-1">
                    <span>Date Added</span>
                    <TableSortIndicator field="created_at" :current-sort="currentSort" />
                  </div>
                </th>
                <th class="px-4 py-3 text-left text-xs font-normal text-black text-opacity-80 cursor-pointer hover:bg-gray-50 select-none" @click="handleSort('user__first_name')">
                  <div class="flex items-center gap-1">
                    <span>Applicant</span>
                    <TableSortIndicator field="user__first_name" :current-sort="currentSort" />
                  </div>
                </th>
                <th class="px-4 py-3 text-left text-xs font-normal text-black text-opacity-80 cursor-pointer hover:bg-gray-50 select-none" @click="handleSort('open_role__employer__name')">
                  <div class="flex items-center gap-1">
                    <span>Company</span>
                    <TableSortIndicator field="open_role__employer__name" :current-sort="currentSort" />
                  </div>
                </th>
                <th class="px-4 py-3 text-left text-xs font-normal text-black text-opacity-80">
                  Match score
                </th>
                <th class="px-4 py-3 text-left text-xs font-normal text-black text-opacity-80 cursor-pointer hover:bg-gray-50 select-none" @click="handleSort('referral_status')">
                  <div class="flex items-center gap-1">
                    <span>Referrer</span>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
v-for="request in store.requests" 
                  :key="request.id" 
                  class="hover:bg-gray-50 cursor-pointer"
                  @click="handleRowClick(request)"
              >
                <!-- Date Added Column -->
                <td class="px-4 py-4 text-sm text-gray-500">
                  {{ formatDate(request.created_at, 'europe') }}
                </td>

                <!-- Applicant Column -->
                <td class="px-4 py-4">
                  <UserDetailsSmall :user_details="request.user_details" />
                </td>

                <!-- Company Column -->
                <td class="px-4 py-4">
                  <div class="flex items-center gap-2">
                    <img
                      :src="request.open_role_details.employer_details.logo"
                      :alt="request.open_role_details.employer_details.name"
                      class="h-5 w-5 object-contain"
                    />
                    <div class="flex flex-col">
                      <span class="text-sm text-gray-900">{{
                        request.open_role_details.employer_details.name
                      }}</span>
                      <a
                        :href="request.open_role_details.url"
                        target="_blank"
                        class="text-sm text-gray-500 hover:text-gray-700 transition-all duration-200 flex items-center gap-1"
                        @click.stop
                      >
                        <span class="truncate max-w-[350px] inline-block">{{
                          request.open_role_details.role_name
                        }}</span>
                        <SquareArrowOutUpRight class="w-4 h-4 inline-block" />
                      </a>
                    </div>
                  </div>
                </td>

                <!-- Match Score Column -->
                <td class="px-4 py-4 text-sm text-center">
                  <span
                    v-if="request.match_score"
                    class="px-2 py-1 rounded-full text-xs font-medium"
                    :class="(request.match_score ?? 0) >= jobMatchingThreshold
                      ? 'bg-lime-100 text-lime-800 border border-lime-200'
                      : 'bg-amber-100 text-amber-800 border border-amber-200'"
                  >
                    {{ request.match_score }}/10
                  </span>
                  <span v-else class="text-gray-400">N/A</span>
                </td>

                <!-- Referrer Column -->
                <td class="text-xs cursor-default">
                  <HoverCard v-if="request.referrer_details">
                    <HoverCardTrigger
                      class="bg-lime-300 text-black text-opacity-60 px-2 py-1 rounded-full cursor-default hover:bg-lime-400 transition-all duration-200"
                    >
                      {{ request.referrer_details.name }}
                    </HoverCardTrigger>
                    <HoverCardContent class="text-sm text-gray-700 text-center">
                      Assigned referrer
                    </HoverCardContent>
                  </HoverCard>
                  <HoverCard
                    v-else-if="request.open_role_details.employer_details.default_referrer_details"
                  >
                    <HoverCardTrigger
                      class="text-gray-500 px-2 py-1 rounded-full border-dashed border-[1px] border-gray-300 hover:bg-gray-100 transition-all duration-200"
                    >
                      {{ request.open_role_details.employer_details.default_referrer_details.name }}
                    </HoverCardTrigger>
                    <HoverCardContent class="text-sm text-gray-700 text-center">
                      Default referrer
                    </HoverCardContent>
                  </HoverCard>
                  <span
                    v-else-if="!request.open_role_details.employer_details.has_available_referrers"
                  >
                    No referrer available
                  </span>
                  <span v-else> Not set </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="store.isPaginated" class="mt-4">
          <PaginationComponent
            :current-page="store.pagination.currentPage"
            :total-pages="store.pagination.totalPages"
            :use-router="true"
            @update:page="handlePageChange"
          />
        </div>
      </div>
    </div>
    </template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useReferralRequestsStore } from '@/stores/referralRequests'
import LoaderComponent from '@/components/shared/LoaderComponent.vue'
import PaginationComponent from '@/components/common/PaginationComponent.vue'
import type { ReferralRequest } from '@/types/referralRequests'
import { SquareArrowOutUpRight, Loader2 } from 'lucide-vue-next'
import { HoverCard, HoverCardTrigger, HoverCardContent } from '@/components/ui/hover-card'
import { formatDate } from '@/utils/formatDate'
import UserDetailsSmall from '@/components/common/UserDetailsSmall.vue'
import { useToast } from '@/components/ui/toast/use-toast'
import TableSortIndicator from '@/components/common/TableSortIndicator.vue'

import { jobMatchingThreshold } from '@/const/jobMatching'

// Define emits
const emit = defineEmits<{
  'row-click': [request: ReferralRequest]
  'request-referral': [request: ReferralRequest]
}>()

const openDropdownId = ref<number | null>(null)
const loadingRequestId = ref<number | null>(null)
const router = useRouter()
const store = useReferralRequestsStore()
const currentSort = computed(() => (store as any).currentSort as string)
const { toast } = useToast()

const handleRowClick = (request: ReferralRequest) => {
  emit('row-click', request)
}

const handlePageChange = async (page: number) => {
  await store.setPage(router, page)
}

const handleRequestReferral = (request: ReferralRequest) => {
  emit('request-referral', request)
}

const toggleDropdown = (requestId: number) => {
  openDropdownId.value = openDropdownId.value === requestId ? null : requestId
}

const handleConfirmRequest = async (request: ReferralRequest) => {
  try {
    loadingRequestId.value = request.id
    await store.updateReferralStatus(String(request.id), 'REFERRAL_SUBMITTED_BY_CHAMPION')
    openDropdownId.value = null
    
    // Show success toast
    toast({
      title: "Referral Confirmed",
      description: "The referral has been confirmed successfully.",
    })
    
    // Refetch the current page data
    await store.fetchRequests({ page: store.pagination.currentPage })
  } catch (error) {
    console.error('Failed to update referral status:', error)
    
    // Show error toast
    toast({
      title: "Error",
      description: "Failed to confirm the referral. Please try again.",
      variant: "destructive",
    })
  } finally {
    loadingRequestId.value = null
  }
}

onMounted(() => {
  // Close dropdown when clicking outside
  document.addEventListener('click', () => {
    openDropdownId.value = null
  })
})

const handleSort = async (field: string) => {
  await store.updateSort(router, field)
}
</script>
